import React, { useContext, useCallback, useEffect, useMemo } from 'react';
import { Grid, CircularProgress, makeStyles, Theme } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { modeUIContext, showSuccessNotification, showDialog, DialogType, DialogResponse, DialogResponseCode, SlideOutPanelLocation,
    showSlideOutPanel,
    hideSlideOutPanel } from '@moderepo/mode-ui-state';
import { RobotCommunicationType, RobotInfo } from '@moderepo/mode-apis';
import { BaseCompAction, capitalizeAll, getRobotDefinitionByRobotClass, SlideOutComp } from '@moderepo/mode-app-components';
import { deleteProjectDevice, ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { deleteRobot, fetchRobotById, rcDataContext, selectRobotById, selectRobotCatalog, updateRobot } from '@moderepo/mode-data-state-robot-cloud';
import { useRitzTranslation } from '../../utils/customHooks';
import { RCIssuePreviewCompOptions, RCMonitorPreviewCompOptions, RCRobotPreviewCompOptions, RCSlideOutPanelCompType } from './RCProjectConfig';
import { RCRouteHelper } from './RCRouteHelper';
import { RCRobotInfoContainer } from './RCRobotInfoContainer';
import { RCRobotStatusContainer } from './RCRobotStatusContainer';
import { RouteHelper } from '../routes/RouteHelper';


const useStyle = makeStyles((theme: Theme) => {
    return {
        primaryAction: {
        },

        secondaryAction: {
            '& .icon': {
                color: theme.palette.primary.main,
            },
            '&.remove, &.unlink, &.create-issue': {
                '& .icon': {
                    color: theme.palette.secondary.main,
                },
            },
        },
    };
});



interface RCRobotPreviewContainerProps {
    readonly projectId: number;
    readonly homeId: number;
    readonly robotId?: string | undefined;
    readonly context?: {
        readonly userId?: number | undefined;
        readonly homeId?: number | undefined;
        readonly deviceId?: number | undefined;
    }
}


/**
 * This container display a preview of a robot details
 */
export const RCRobotPreviewContainer = (props: RCRobotPreviewContainerProps) => {
    const navigate = useNavigate();
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const { projectId, homeId, robotId } = props;
    const project = selectProjectById(dataState, projectId);
    const robotCatalog = selectRobotCatalog(rcDataState, projectId);

    const selectedRobot = selectRobotById(rcDataState, homeId, robotId);
    const isNewRobot = !robotId;
    const routeHelper = RouteHelper.getInstance(navigate);
    const rcRouteHelper = RCRouteHelper.getInstance(navigate);

    const { trans } = useRitzTranslation(project);
    const classes = useStyle();


    // Find the robot definition for the current selected robot
    const selectedRobotDefinition = useMemo(() => {
        return getRobotDefinitionByRobotClass(robotCatalog, selectedRobot?.robotClass);
    }, [selectedRobot, robotCatalog]);

    const isGatewayRobot = useMemo(() => {
        return selectedRobotDefinition?.modeCommunicationType === RobotCommunicationType.GATEWAY;
    }, [selectedRobotDefinition]);



    /**
     * Update the selected entity to select a different robot or unselect a robot
     */
    const updateSelectedPreviewRobot = useCallback((selectedRobotId: string | undefined) => {
        if (selectedRobotId) {
            const options: RCRobotPreviewCompOptions = {
                location : SlideOutPanelLocation.RIGHT,
                compType : RCSlideOutPanelCompType.ROBOT_PREVIEW,
                compProps: {
                    data: {
                        projectId: props.projectId,
                        homeId   : props.homeId,
                        robotId  : selectedRobotId,
                    },
                    context: props.context,
                },
            };
            uiDispatch(showSlideOutPanel(options));
        } else {
            uiDispatch(hideSlideOutPanel());
        }
    }, [props.homeId, props.context, props.projectId, uiDispatch]);



    // This useEffect is used for loading Robot data for the selected robot if we don't have the data yet
    useEffect(() => {
        if (props.robotId && !selectedRobot) {
            rcDataDispatch(fetchRobotById(projectId, homeId, props.robotId)).catch(() => {
                // ignore error because dataDispatch already handled it
            });
        }
    }, [props.robotId, projectId, homeId, selectedRobot, rcDataDispatch]);



    /**
     * On Unlink Robot button click. We will remove the robot from the device it is tied to. This will NOT delete the robot from the home.
     */
    const onUnlinkRobotClicked = useCallback((robot: RobotInfo) => {
        // Confirm before deleting a Robot
        uiDispatch(showDialog({
            type          : DialogType.CONFIRM,
            title         : 'dialog.confirm_unlink_robot.title',
            message       : 'dialog.confirm_unlink_robot.message',
            messageData   : { robot_name: robot.name || robot.id },
            positiveButton: 'label.unlink',
            negativeButton: 'label.cancel',
            onClose       : async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    try {
                        await rcDataDispatch(updateRobot(projectId, homeId, robot.id, { deviceId: null }));

                        uiDispatch(showSuccessNotification('notif.generic_item_removed_successfully', { term: 'label.robot' }));

                        // if the current selected robot is deleted, unselect it.
                        updateSelectedPreviewRobot(undefined);
                    } catch (error) {
                        // ignore error
                    }
                }
            },
        }));
    }, [uiDispatch, rcDataDispatch, projectId, homeId, updateSelectedPreviewRobot]);



    /**
     * On delete robot button click. This will delete the robot completely from the home.
     */
    const onDeleteRobotClicked = useCallback((robot: RobotInfo) => {
        // Confirm before deleting a Robot
        uiDispatch(showDialog({
            type          : DialogType.CONFIRM,
            title         : 'dialog.confirm_delete_robot.title',
            message       : 'dialog.confirm_delete_robot.message',
            messageData   : { robot_name: robot.name || robot.id },
            positiveButton: 'label.delete',
            negativeButton: 'label.cancel',
            onClose       : async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    try {
                        await rcDataDispatch(deleteRobot(projectId, homeId, robot.id));

                        // If Robot is gateway-less, we will also need to delete the device associated with it
                        if (robot.deviceId && selectedRobotDefinition?.modeCommunicationType === RobotCommunicationType.DIRECT
                            && project?.onDemandProvisioning) {
                            await dataDispatch(deleteProjectDevice(projectId, robot.deviceId));
                        }

                        uiDispatch(showSuccessNotification('notif.generic_item_deleted_successfully', { term: 'label.robot' }));

                        // if the current selected robot is deleted, unselect it.
                        updateSelectedPreviewRobot(undefined);
                    } catch (error) {
                        // ignore error
                    }
                }
            },
        }));
    }, [uiDispatch, rcDataDispatch, projectId, homeId, selectedRobotDefinition, project, updateSelectedPreviewRobot, dataDispatch]);



    /**
     * Unselect the selected robot and update the URL
     */
    const unselectRobot = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
    }, [uiDispatch]);


    /**
     * On View Robot Details button click, take the user to the Robot details page
     */
    const onViewRobotDetails = useCallback((robotHomeId: number, rId: string) => {
        rcRouteHelper.gotoRobotPage({ projectId, homeId: robotHomeId, robotId: rId, ...props.context });
        uiDispatch(hideSlideOutPanel());
    }, [projectId, props.context, rcRouteHelper, uiDispatch]);



    /**
     * On View Device Details button click, take the user to the device details page
     */
    const onViewDeviceDetails = useCallback((robot: RobotInfo) => {
        if (robot.deviceId) {
            routeHelper.gotoDevicePage({ projectId, homeId: robot.homeId, deviceId: robot.deviceId });
        }
    }, [projectId, routeHelper]);


    /**
     * On newRobotCanceled event from RobotInfoComp, hide the preview panel
     */
    const onNewRobotCanceled = useCallback(() => {
        unselectRobot();
    }, [unselectRobot]);



    /**
     * onNewRobotCreated event from RobotInfoComp, show the new Robot in the preview panel
     */
    const onNewRobotCreated = useCallback(() => {
        updateSelectedPreviewRobot(undefined);
    }, [updateSelectedPreviewRobot]);


    /**
     * On Create Monitor for the selected robot's class. We will trigger a create Monitor and pass the selected robot's robotClass
     * to the create monitor panel
     */
    const onCreateMonitorRobotClassClicked = useCallback((robot: RobotInfo) => {
        const options: RCMonitorPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : RCSlideOutPanelCompType.MONITOR_PREVIEW,
            compProps: {
                data: {
                    projectId,
                    preSelectedRobotClass: robot.robotClass,
                },
                context: props.context,
            },
        };

        uiDispatch(showSlideOutPanel(options));
    }, [projectId, props.context, uiDispatch]);


    /**
     * On Create Monitor for the selected robot's class for the robot's home only. We will trigger a create Monitor and pass the selected robot's
     * robotClass and homeId to the create monitor panel
     */
    const onCreateMonitorHomeRobotClassClicked = useCallback((robot: RobotInfo) => {
        const options: RCMonitorPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : RCSlideOutPanelCompType.MONITOR_PREVIEW,
            compProps: {
                data: {
                    projectId,
                    preSelectedRobotClass: robot.robotClass,
                    preSelectedHomeId    : robot.homeId,
                },
                context: props.context,
            },
        };

        uiDispatch(showSlideOutPanel(options));
    }, [props.context, projectId, uiDispatch]);


    /**
     * On Create Monitor for the selected robot. We will trigger a create Monitor and pass the selected robot's id and homeId
     * to the create monitor panel
     */
    const onCreateMonitorRobotIdClicked = useCallback((robot: RobotInfo) => {
        const options: RCMonitorPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : RCSlideOutPanelCompType.MONITOR_PREVIEW,
            compProps: {
                data: {
                    projectId,
                    preSelectedRobotId: robot.id,
                    preSelectedHomeId : robot.homeId,
                },
                context: props.context,
            },
        };

        uiDispatch(showSlideOutPanel(options));
    }, [props.context, projectId, uiDispatch]);



    /** Shortcut to create an Issue for the selected robot  */
    const onCreateRobotIssueClicked = useCallback((robot: RobotInfo) => {
        const options: RCIssuePreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : RCSlideOutPanelCompType.ISSUE_PREVIEW,
            compProps: {
                data: {
                    projectId,
                    preSelectedRobotId: robot.id,
                    preSelectedHomeId : robot.homeId,
                },
                context: props.context,
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [projectId, props.context, uiDispatch]);



    /**
     * use useMemo to create the the newRobotSettings so that this doesn't get created on every re-render
     */
    const newRobotSettings = useMemo(() => {
        return {
            onNewRobotCreated,
            onNewRobotCanceled,
        };
    }, [onNewRobotCreated, onNewRobotCanceled]);


    const primaryActions = useMemo(() => {
        if (selectedRobot) {
            const actions: BaseCompAction[] = [{
                className: clsx(classes.primaryAction),
                label    : capitalizeAll(trans('label.view_robot_details')),
                onClick  : () => {
                    onViewRobotDetails(selectedRobot.homeId, selectedRobot.id);
                },
            }];

            return actions;
        }

        return undefined;
    }, [classes.primaryAction, onViewRobotDetails, selectedRobot, trans]);


    const secondaryActions = useMemo(() => {
        if (selectedRobot) {
            const actions: (BaseCompAction | null | undefined)[] = [];

            if (isGatewayRobot) {
                actions.push({
                    className: clsx(classes.secondaryAction),
                    icon     : 'info',
                    label    : capitalizeAll(trans(isGatewayRobot ? 'label.view_gateway_details' : 'label.view_device_details')),
                    onClick  : () => {
                        onViewDeviceDetails(selectedRobot);
                    },
                });
            }

            actions.push({
                className: clsx(classes.secondaryAction),
                icon     : 'visibility',
                label    : capitalizeAll(trans('label.create_monitor_for_robot_class')),
                onClick  : () => {
                    onCreateMonitorRobotClassClicked(selectedRobot);
                },
            });

            actions.push({
                className: clsx(classes.secondaryAction),
                icon     : 'visibility',
                label    : capitalizeAll(trans('label.create_monitor_for_home_robot_class')),
                onClick  : () => {
                    onCreateMonitorHomeRobotClassClicked(selectedRobot);
                },
            });

            actions.push({
                className: clsx(classes.secondaryAction),
                icon     : 'visibility',
                label    : capitalizeAll(trans('label.create_monitor_for_robot')),
                onClick  : () => {
                    onCreateMonitorRobotIdClicked(selectedRobot);
                },
            });

            actions.push({
                className: clsx(classes.secondaryAction, 'create-issue'),
                icon     : 'report',
                label    : capitalizeAll(trans('label.create_issue')),
                onClick  : () => {
                    onCreateRobotIssueClicked(selectedRobot);
                },
            });

            // add a divider
            actions.push(undefined);

            if (selectedRobot.deviceId && selectedRobotDefinition?.modeCommunicationType === RobotCommunicationType.GATEWAY) {
                actions.push({
                    className: clsx(classes.secondaryAction, 'unlink'),
                    icon     : 'link_off',
                    label    : capitalizeAll(trans(isGatewayRobot
                        ? 'label.unlink_robot_from_gateway' : 'label.unlink_robot_from_device')),
                    onClick: () => {
                        onUnlinkRobotClicked(selectedRobot);
                    },
                });
            }

            actions.push({
                className: clsx(classes.secondaryAction, 'remove'),
                icon     : 'delete',
                label    : capitalizeAll(trans('label.delete_robot')),
                onClick  : () => {
                    onDeleteRobotClicked(selectedRobot);
                },
            });

            return actions;
        }

        return undefined;
    }, [classes.secondaryAction, isGatewayRobot, onCreateMonitorHomeRobotClassClicked, onCreateMonitorRobotClassClicked,
        onCreateMonitorRobotIdClicked, onCreateRobotIssueClicked, onDeleteRobotClicked, onUnlinkRobotClicked, onViewDeviceDetails, selectedRobot,
        selectedRobotDefinition, trans]);


    const slideOutCompContent = useMemo(() => {

        if (selectedRobot || !props.robotId) {
            return (
                <Grid container spacing={2}>
                    {/* Show Robot Status if there is at least 1 alert status. If robot's condition is healthy, don't need to show the status */}
                    {selectedRobot && selectedRobot.status && selectedRobot.status.alertStatuses.length > 0 && (
                        <Grid item xs={12}>
                            <RCRobotStatusContainer
                                projectId={projectId}
                                title={capitalizeAll(trans('label.status'))}
                                homeId={selectedRobot.homeId}
                                robotId={selectedRobot.id}
                                robotClass={selectedRobot.robotClass}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <RCRobotInfoContainer
                            className="transparent"
                            infoCompProps={{
                                formProps: {
                                    className: 'single-column',
                                },
                            }}
                            projectId={projectId}
                            robotId={robotId}
                            homeId={homeId}
                            targetGatewayId={!robotId ? props.context?.deviceId : undefined}
                            newRobotSettings={
                                isNewRobot ? newRobotSettings : undefined
                            }
                            minimalUI
                        />
                    </Grid>
                </Grid>
            );
        }

        return (
            <CircularProgress />
        );
    }, [homeId, isNewRobot, newRobotSettings, projectId, props.context, props.robotId, robotId, selectedRobot, trans]);


    return (
        <SlideOutComp
            compContent={slideOutCompContent}
            primaryActions={primaryActions}
            secondaryActions={secondaryActions}
            closable={!isNewRobot}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
